import { apiFetch, apiFetchRaw } from "@/lib/apiClient";

/**
 * Client for app/modules/imaging_studies/router.py — mounted at
 * /api/patient/imaging-studies. Fully separate from the generic
 * document/zip pipeline (dataPipelineApi.ts): its own table, own S3 prefix,
 * own DICOM-specific validation. Patient-wide (not per-encounter/per-record)
 * — GET / returns every uploaded study for the acting patient, matching how
 * the backend itself scopes this resource (see get_effective_patient_id_for_viewing).
 *
 * Same `actingAsProfileId` convention as dataPipelineApi.ts — the backend
 * dependencies backing every one of these routes (get_effective_patient_id /
 * get_effective_patient_id_for_viewing) read the exact same
 * X-Acting-As-Profile-Id header, so switching to a Dedicated Profile scopes
 * imaging studies the same way it already scopes uploads/records.
 */
function actingAsHeaders(actingAsProfileId?: string): Record<string, string> {
  return actingAsProfileId ? { "X-Acting-As-Profile-Id": actingAsProfileId } : {};
}

const BASE_PATH = "/api/patient/imaging-studies";

// No "duplicate" status — duplicate SOPInstanceUID uploads are allowed
// (always stored as a new row), matching the generic document/upload
// pipeline's own behavior. See imaging_studies/service.py's module docstring.
export type ImagingUploadStatus = "success" | "failed";

/** One file's outcome from a batch upload — real shape confirmed against
 *  the backend's UploadResultItem (imaging_studies/schemas.py): filename,
 *  status, imaging_study_id, reason. No index/position field is sent — a
 *  streamed-in result can only be matched back to its staged row by
 *  filename (see ImagingUploadModal.tsx's matchResult, and its own note on
 *  why that's an inherent limitation for two same-named files in one
 *  batch). */
export interface ImagingUploadResult {
  filename: string;
  status: ImagingUploadStatus;
  imaging_study_id?: string | null;
  reason?: string | null;
}

/** One uploaded DICOM study's metadata — no pixel data. Matches
 *  imaging_studies/schemas.py's ImagingStudyOut exactly (camelCase, the
 *  app's usual convention for GET responses). */
export interface ImagingStudy {
  id: string;
  studyInstanceUid: string;
  seriesInstanceUid: string;
  sopInstanceUid: string;
  modality: string | null;
  numberOfFrames: number | null;
  rows: number | null;
  columns: number | null;
  photometricInterpretation: string | null;
  bitsAllocated: number | null;
  pixelRepresentation: number | null;
  transferSyntaxUid: string | null;
  originalFilename: string;
  fileSizeBytes: number;
  uploadStatus: string;
  confidenceFlags: string[];
  createdAt: string;
  /** The patient-entered label from the upload modal's "Image Title" field —
   *  null for anything uploaded before this field existed, or left blank.
   *  Display code should fall back to originalFilename when this is null. */
  title: string | null;
}


// "Recent DICOM Uploads" + the paginated study list below it are both a
// recent-activity view, not a records table — 10 per page mirrors
// dataPipelineApi.ts's UPLOADS_PAGE_SIZE convention for the same kind of
// list, sized independently of it (see imaging_studies/router.py's own
// _IMAGING_STUDIES_DEFAULT_LIMIT comment).
export const IMAGING_STUDIES_PAGE_SIZE = 10;

/** Thrown by every function below on a non-2xx/network failure. `status` is
 *  0 for a network-level failure (never reached the server) — callers can
 *  use that to tell a real rejection apart from connectivity/timeout. */
export class ImagingStudiesApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/**
 * Confirmed against the real backend (imaging_studies/router.py +
 * schemas.py):
 *
 *   POST /api/patient/imaging-studies/upload   (multipart: files[], title?)
 *     -> { batch_id, total_expected }           (UploadBatchCreateResponse)
 *   GET  /api/patient/imaging-studies/batches/{batch_id}
 *     -> ImagingBatchStatusResponse
 *
 * Unlike the Records upload flow (dataPipelineApi.ts's uploadMedicalZip),
 * there is NO client_request_id support and NO by-client-id recovery
 * endpoint here — that was this module's own earlier addition, mirrored
 * from the Records flow before the real contract was confirmed, and it
 * doesn't exist on this backend. If the POST's own response is lost to a
 * network error, there is currently no way to recover the batch_id — see
 * ImagingUploadModal.tsx's handling of ImagingBatchUploadError.networkError,
 * which surfaces a clear message instead of guessing.
 */
export type ImagingBatchStatus = "queued" | "processing" | "completed" | "failed";

export interface ImagingBatchAccepted {
  batch_id: string;
  total_expected: number;
}

export interface ImagingBatchStatusResponse {
  batch_id: string;
  status: ImagingBatchStatus;
  total_expected: number;
  /** How many of total_expected have a terminal per-file result so far —
   *  grows monotonically while status is "processing". */
  processed_count: number;
  /** Per-file results that have landed so far, in no particular guaranteed
   *  order — rendered as a running list, matched onto staged rows by
   *  filename (a batch response, unlike the old synchronous one, can't be
   *  matched by array position since results stream in out of order). */
  results: ImagingUploadResult[];
}

/** Thrown by uploadImagingStudyBatch. `networkError` tells the caller
 *  whether the request itself never got an answer (true — the batch may
 *  have been accepted anyway, but there is no way to confirm that; see this
 *  module's own note above on the missing client-id recovery path) versus
 *  the server actively rejecting the request before creating anything
 *  (false). */
export class ImagingBatchUploadError extends Error {
  networkError: boolean;
  constructor(message: string, networkError: boolean) {
    super(message);
    this.networkError = networkError;
  }
}

/** Submits one or more DICOM files (or a zip of them) as a single async
 *  batch — POST /upload (same path the old synchronous endpoint used;
 *  only its response shape changed). Resolves immediately with just
 *  {batch_id, total_expected} — NO per-file results yet. Callers must poll
 *  getImagingBatchStatus for progress and final per-file outcomes. */
export async function uploadImagingStudyBatch(
  files: File[],
  actingAsProfileId?: string,
  /** The upload modal's one "Image Title" field — applied to every file in
   *  this batch (one title, however many files are staged together). */
  title?: string,
): Promise<ImagingBatchAccepted> {
  const formData = new FormData();
  for (const file of files) formData.append("files", file);
  if (title && title.trim()) formData.append("title", title.trim());

  const { data, error, status, networkError } = await apiFetch<ImagingBatchAccepted>(`${BASE_PATH}/upload`, {
    method: "POST",
    body: formData,
    portal: "patient",
    headers: actingAsHeaders(actingAsProfileId),
    // Multiple DICOM files (or one zip) can comfortably exceed apiClient's
    // default 30s timeout even just to be accepted/stored — the actual
    // validation/decoding now happens later, off the request, but streaming
    // the upload itself in is still a real transfer.
    timeoutMs: 5 * 60_000,
  });

  if (error) {
    throw new ImagingBatchUploadError(error.message || "Upload failed", networkError);
  }
  if (!data) {
    throw new ImagingBatchUploadError("Received an empty or invalid response. Please try again.", false);
  }
  return data;
}

/**
 * ASSUMED BACKEND CONTRACT — NOT YET CONFIRMED. This endpoint is being built
 * in parallel by another agent — CONFIRMED against the real backend
 * (imaging_studies/router.py's GET /batches + schemas.py's
 * ImagingBatchSummaryOut/ImagingBatchListResponse) and reconciled below. The
 * path, query param shape, and envelope all matched the original assumption
 * exactly; only the per-item field casing needed correcting:
 *
 *   GET /api/patient/imaging-studies/batches?status=queued,processing
 *     -> { success, data: ImagingBatchSummary[], count, hasMore }
 *
 * ImagingBatchSummary's fields are snake_case, NOT this file's usual
 * camelCase GET convention — confirmed deliberate on the backend side: this
 * schema mirrors the sibling "batch" family (ImagingBatchStatusResponse/
 * UploadBatchCreateResponse), which has always been snake_case here, rather
 * than the ImagingStudy/ImagingSeries GET convention.
 *
 * Used by ImagingStudiesContext.tsx to resume tracking of any batch still
 * in flight server-side when the provider (re)mounts — a page reload, or a
 * sign-out/sign-in cycle, both of which previously lost all live-progress
 * tracking even though the backend worker itself was still processing the
 * batch. See that file's resume-on-mount effect.
 */
export interface ImagingBatchSummary {
  batch_id: string;
  /** The patient-entered "Image Title" if one was given at submit time,
   *  else null — same field this module's ImagingStudy.title already
   *  documents the same fallback rule for. Display-name fallback when null,
   *  per the backend's own documented contract: "{total_expected} files". */
  title: string | null;
  status: ImagingBatchStatus;
  total_expected: number;
  processed_count: number;
  /** Per-file results streamed in so far — not needed by the resume flow
   *  (which only cares about status/counts), but included since the real
   *  backend always sends it. */
  results: ImagingUploadResult[];
  created_at: string;
}

export interface ImagingActiveBatchListResponse {
  success: boolean;
  data: ImagingBatchSummary[];
  count: number;
  hasMore: boolean;
}

/** ASSUMED endpoint (see ImagingBatchSummary's own docstring above) —
 *  fetches batches still in flight (queued/processing) for the acting
 *  patient, so ImagingStudiesContext can resume tracking them on mount. */
export async function listActiveImagingBatches(
  actingAsProfileId?: string,
): Promise<ImagingActiveBatchListResponse> {
  const { data, error, status } = await apiFetch<ImagingActiveBatchListResponse>(
    `${BASE_PATH}/batches?status=queued,processing`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(actingAsProfileId) },
  );
  if (error) {
    throw new ImagingStudiesApiError(status, error.message || "Failed to load in-progress imaging batches.");
  }
  if (!data) {
    throw new ImagingStudiesApiError(status, "Received an empty or invalid response.");
  }
  return data;
}

/** Polls the live status of a previously-submitted batch. */
export async function getImagingBatchStatus(
  batchId: string,
  actingAsProfileId?: string,
): Promise<ImagingBatchStatusResponse> {
  const { data, error, status } = await apiFetch<ImagingBatchStatusResponse>(`${BASE_PATH}/batches/${batchId}`, {
    method: "GET",
    portal: "patient",
    headers: actingAsHeaders(actingAsProfileId),
  });
  if (error) {
    throw new ImagingStudiesApiError(status, error.message || "Failed to fetch batch status");
  }
  return data as ImagingBatchStatusResponse;
}

/**
 * Confirmed against the real backend's ImagingSeriesGroupOut (imaging_studies/
 * schemas.py): the list endpoint returns one entry per series_instance_uid
 * (a "series" = one or more files that share it — a multi-file series, or a
 * single file that may itself be multi-frame), instead of one row per raw
 * uploaded file. `files` (named `files` on the backend — NOT `members`) is
 * ordered the way the viewer should step through them (currentIndex in
 * DicomViewer.tsx maps directly onto this array for a multi-file series).
 * frameCount is the total displayable frame count across every file (sum of
 * each file's own numberOfFrames, defaulting to 1) — what the scroller/cine
 * control in Phase 3 steps across; fileCount is just files.length, surfaced
 * separately since the list card shows both ("3 files, 3 frames" vs "1
 * file, 40 frames" read very differently to a patient). Each entry in
 * `files` is a full ImagingStudyOut — identical shape to the existing
 * `ImagingStudy` type above (same backend schema), reused here rather than
 * duplicated. */
export type ImagingSeriesMember = ImagingStudy;

export interface ImagingSeries {
  seriesInstanceUid: string;
  studyInstanceUid: string;
  modality: string | null;
  title: string | null;
  createdAt: string;
  fileCount: number;
  frameCount: number;
  files: ImagingSeriesMember[];
}

/**
 * A stable identifier for ONE returned group — use this for React `key`s,
 * dedup (usePaginatedList's `getId`), and "which row did the patient click"
 * lookups; NEVER `seriesInstanceUid` alone for that. Why: as of the
 * same-upload-batch scoping fix in imaging_studies/service.py's
 * list_series_grouped, two DIFFERENT groups in one response CAN legitimately
 * share the same seriesInstanceUid tag — that's the exact real-world case
 * this fix targets (e.g. sample/test DICOM files reused across separate
 * uploads, which genuinely share a real SeriesInstanceUID tag but must never
 * be treated as "the same series" any more). `seriesInstanceUid` alone is
 * therefore no longer guaranteed unique across a list response's entries,
 * even though it's still perfectly fine to DISPLAY.
 *
 * files[0].id is the group's newest member's own row id (a real UUID
 * primary key, always unique) — used instead. The `seriesInstanceUid`
 * fallback only matters for a defensively-malformed/empty `files` array,
 * which the backend never actually sends (every group has >=1 member).
 */
export function getSeriesGroupId(series: ImagingSeries): string {
  return series.files[0]?.id ?? series.seriesInstanceUid;
}

/** Matches the backend's own ImagingStudyListResponse name exactly (its
 *  `data` array holds ImagingSeriesGroupOut rows, per the real contract). */
export interface ImagingStudyListResponse {
  success: boolean;
  data: ImagingSeries[];
  count: number;
  hasMore: boolean;
  totalCount: number;
}

/** One page of the acting patient's uploaded imaging studies, grouped by
 *  series (see ImagingSeries above), newest first per the backend's own
 *  ordering. Returns the full envelope (not just `.data`) so a caller can
 *  read `hasMore`/`totalCount` — see usePaginatedList's PagedResult shape,
 *  which this satisfies directly. */
export async function listImagingSeries(
  actingAsProfileId?: string,
  options?: { limit?: number; offset?: number },
): Promise<ImagingStudyListResponse> {
  const params = new URLSearchParams();
  params.set("limit", String(options?.limit ?? IMAGING_STUDIES_PAGE_SIZE));
  params.set("offset", String(options?.offset ?? 0));

  const { data, error, status } = await apiFetch<ImagingStudyListResponse>(`${BASE_PATH}?${params.toString()}`, {
    method: "GET",
    portal: "patient",
    headers: actingAsHeaders(actingAsProfileId),
  });

  if (error) {
    throw new ImagingStudiesApiError(status, error.message || "Failed to load imaging studies.");
  }
  // Same reasoning as before: a 2xx response with an unparseable body is a
  // real failure, not "this patient genuinely has zero studies" — treating
  // it as the latter is exactly what silently blanked out an
  // already-populated list on a transient hiccup (a slow dev-server reload
  // mid-response, a flaky connection, ...). Let the caller's error state
  // handle it and keep showing whatever was already loaded, instead of
  // usePaginatedList confidently replacing good data with a fake "empty"
  // page.
  if (!data) {
    throw new ImagingStudiesApiError(status, "Received an empty or invalid response. Please try again.");
  }
  return data;
}

export async function getImagingStudy(id: string, actingAsProfileId?: string): Promise<ImagingStudy> {
  const { data, error, status } = await apiFetch<ImagingStudy>(`${BASE_PATH}/${id}`, {
    method: "GET",
    portal: "patient",
    headers: actingAsHeaders(actingAsProfileId),
  });

  if (error) {
    throw new ImagingStudiesApiError(status, error.message || "Failed to load imaging study.");
  }
  return data as ImagingStudy;
}

export async function deleteImagingStudy(id: string, actingAsProfileId?: string): Promise<void> {
  const { error, status } = await apiFetch<{ success: boolean }>(`${BASE_PATH}/${id}`, {
    method: "DELETE",
    portal: "patient",
    headers: actingAsHeaders(actingAsProfileId),
  });

  if (error) {
    throw new ImagingStudiesApiError(status, error.message || "Failed to delete imaging study.");
  }
}

/** Which failure mode fetching the raw DICOM file hit — the viewer needs to
 *  tell these apart (see DicomViewer): "network"/"timeout" and "auth"
 *  are failures BEFORE dwv ever sees any bytes, categorically different from
 *  a "decode" failure (dwv itself rejecting bytes it successfully received). */
export type ImagingFileErrorKind = "network" | "auth" | "not_found" | "other";

export class ImagingFileError extends Error {
  kind: ImagingFileErrorKind;
  constructor(message: string, kind: ImagingFileErrorKind) {
    super(message);
    this.kind = kind;
  }
}

/**
 * Fetches one study's raw DICOM bytes as a Blob, through the same
 * authenticated client (apiFetchRaw) every other PHI-carrying call in this
 * app uses — GET /{id}/file is auth-gated (bearer token + cookies), so a
 * plain unauthenticated fetch/dwv.loadURLs() would 401. The caller hands the
 * resulting Blob to dwv via app.loadFiles([new File(...)]), never a URL.
 *
 * Throws ImagingFileError with a `kind` that lets the viewer distinguish a
 * network/timeout/auth failure (nothing was ever decoded) from a later dwv
 * decode failure (bytes arrived fine, dwv itself rejected them).
 */
export async function getImagingStudyFileBlob(id: string, actingAsProfileId?: string): Promise<Blob> {
  let res: Response;
  try {
    res = await apiFetchRaw(`${BASE_PATH}/${id}/file`, {
      method: "GET",
      portal: "patient",
      headers: actingAsHeaders(actingAsProfileId),
      // A multi-frame DICOM file can be large; give it more room than the
      // default 30s before calling it a network failure.
      timeoutMs: 2 * 60_000,
    });
  } catch (err) {
    const timedOut = err instanceof DOMException && err.name === "AbortError";
    throw new ImagingFileError(
      timedOut
        ? "The request took too long. Please check your connection and try again."
        : "Unable to connect. Please check your internet connection and try again.",
      "network",
    );
  }

  if (res.status === 401 || res.status === 403) {
    throw new ImagingFileError(
      "Your session has expired or you no longer have access to this file. Please sign in again.",
      "auth",
    );
  }
  if (res.status === 404) {
    throw new ImagingFileError("This imaging study could not be found.", "not_found");
  }
  if (!res.ok) {
    throw new ImagingFileError("Unable to load this file right now. Please try again.", "other");
  }
  return res.blob();
}

/**
 * Confirmed against the real backend (imaging_studies/schemas.py):
 * `AnnotationStroke { tool: "pen" | "eraser", color: string, points: {x,y}[] }`
 * — points are IMAGE-INTRINSIC pixel coordinates (column/row), independent
 * of whatever zoom/pan/canvas size was in effect while the patient drew it.
 * Converting from on-screen canvas coordinates to this space is done by the
 * viewer via dwv's ViewLayer.displayToPlaneIndex() before ever calling
 * saveImagingAnnotations — see DicomViewer.tsx.
 */
export interface AnnotationPoint {
  x: number;
  y: number;
}

export interface AnnotationStroke {
  tool: "pen" | "eraser";
  color: string;
  /** Screen pixels at the time of drawing — purely cosmetic (eraser stroke
   *  thickness, pen thickness); not part of the coordinate-space contract
   *  above, and not a field the backend stores per-stroke. */
  width: number;
  points: AnnotationPoint[];
}

/** Matches the backend's ImagingAnnotationOut exactly — the GET/PUT
 *  response shape. `frameIndex` is always a concrete int (0 for a
 *  single-frame file's only frame — the annotation table's unique
 *  constraint is on (imaging_study_id, frame_index), never null there), or
 *  a multi-frame file's intra-file frame number (see DicomViewer's
 *  currentIndex). */
export interface ImagingAnnotationOut {
  imagingStudyId: string;
  frameIndex: number;
  strokes: AnnotationStroke[];
  updatedAt: string | null;
  createdBy: string | null;
}

/** GET /api/patient/imaging-studies/{id}/annotations?frame_index= — the full
 *  saved stroke set for one exact file+frame. Returns an empty stroke list
 *  (never throws ImagingStudiesApiError(404, ...)) when nothing has been
 *  saved for this frame yet — "no annotations yet" is not a failure.
 *  `signal` lets the caller cancel a request it no longer needs (it then
 *  owns cancellation entirely — apiFetch's own default timeout is skipped). */
export async function getImagingAnnotations(
  imagingStudyId: string,
  frameIndex: number,
  actingAsProfileId?: string,
  signal?: AbortSignal,
): Promise<ImagingAnnotationOut> {
  const { data, error, status } = await apiFetch<ImagingAnnotationOut>(
    `${BASE_PATH}/${imagingStudyId}/annotations?frame_index=${frameIndex}`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(actingAsProfileId), signal },
  );
  if (error) {
    if (status === 404) return { imagingStudyId, frameIndex, strokes: [], updatedAt: null, createdBy: null };
    throw new ImagingStudiesApiError(status, error.message || "Failed to load annotations.");
  }
  return data ?? { imagingStudyId, frameIndex, strokes: [], updatedAt: null, createdBy: null };
}

/** PUT /api/patient/imaging-studies/{id}/annotations?frame_index= — replaces
 *  the FULL saved stroke set for this exact file+frame with `strokes` (an
 *  explicit "Save" action in the viewer, never autosaved per-stroke — see
 *  DicomViewer.tsx). Request body is just `{ strokes }` (ImagingAnnotationIn)
 *  — batch/patient identity comes from auth + the URL, not the body. */
export async function saveImagingAnnotations(
  imagingStudyId: string,
  frameIndex: number,
  strokes: AnnotationStroke[],
  actingAsProfileId?: string,
): Promise<ImagingAnnotationOut> {
  const { data, error, status } = await apiFetch<ImagingAnnotationOut>(
    `${BASE_PATH}/${imagingStudyId}/annotations?frame_index=${frameIndex}`,
    {
      method: "PUT",
      portal: "patient",
      headers: { ...actingAsHeaders(actingAsProfileId), "Content-Type": "application/json" },
      body: JSON.stringify({ strokes }),
    },
  );
  if (error) {
    throw new ImagingStudiesApiError(status, error.message || "Failed to save annotations.");
  }
  return data ?? { imagingStudyId, frameIndex, strokes, updatedAt: null, createdBy: null };
}
